import { Minus, Plus, PlusCircleSolid, TrashSolid } from '@byway/icons';
import clsx from 'clsx';
import { useLocale, useTranslations } from 'next-intl';
import { useEffect, useState } from 'react';
import { Button } from 'react-aria-components';
import { useDebounce } from 'use-debounce';
import { useInteractiveLoader } from '~/components/PartnerLoader/PartnerLoader';
import type { Locale } from '~/i18n/i18n-config';
import { api } from '~/libs/api';
import { usePlaygroundActions, usePlaygroundUIState } from '~/stores/playgroundStore';
import type { JourneyTransformed } from '~/types/Journey.types';
import type { StayTransformed } from '~/types/JourneyItem.types';
import { logError, trackEvent } from '~/utils';
import styles from './NightsAdjuster.module.css';
interface NightsAdjusterProps {
  item: StayTransformed | null; // null when no stay exists (add stay mode)
  journeyId: string;
  atStart?: boolean;
  atEnd?: boolean;
  placeName?: string; // name of the place (for add stay mode)
  skeletonTripId: string;
  isDisabled?: boolean;
}
export function NightsAdjuster({
  item,
  journeyId,
  skeletonTripId,
  atStart = false,
  atEnd = false,
  placeName,
  isDisabled
}: NightsAdjusterProps) {
  const t = useTranslations();
  const locale = useLocale() as Locale;
  const [localNights, setLocalNights] = useState(item?.nights ?? 1);
  const [isLoading, setIsLoading] = useState(false);
  const [debouncedNights, setDebouncedNights] = useDebounce(localNights, 2000);
  const {
    setSkeletonTrip,
    setFullTrip
  } = usePlaygroundActions();
  const {
    startLoader,
    completeProgressBar
  } = useInteractiveLoader();
  const {
    isOverviewDisabled,
    isOverviewSubmitting,
    tripPanelResult
  } = usePlaygroundUIState();
  const {
    setOverviewDisabled,
    setOverviewSubmitting,
    setTripPanelResult
  } = usePlaygroundActions();
  const changeSkeletonDuration = api.playground.changeSkeletonDuration.useMutation();
  const addStayToSkeletonMutation = api.playground.addStayToSkeleton.useMutation();
  const removeStayFromSkeletonMutation = api.playground.removeStayFromSkeleton.useMutation();
  const isAddStayEnabled = process.env.NEXT_PUBLIC_FF_ADD_STAY_IN_MAPS === 'true';
  const isAddStayMode = !item;
  const displayPlaceName = placeName || (atStart ? 'start' : 'end');
  const isMinReached = item ? localNights <= 1 : false;
  const isMaxReached = localNights >= 30;
  const areEditsDisabled = isDisabled || localNights === item?.nights && isOverviewDisabled || isOverviewSubmitting;
  const hasThisPlaceFailed = tripPanelResult?.type === 'update-nights' && tripPanelResult.status === 'failure' && item && tripPanelResult.id === item.id;
  const canRemoveStay = localNights === 1 && item && (atStart || atEnd);
  const handleAddStay = async () => {
    setIsLoading(true);
    setTripPanelResult(null);
    setOverviewDisabled(true);
    setOverviewSubmitting(true);
    startLoader();
    try {
      const result = await addStayToSkeletonMutation.mutateAsync({
        journeyId: skeletonTripId,
        atStart,
        atEnd
      });
      const success = result.success && result.journey;
      if (success) {
        // TODO - change to skeleton trip
        setSkeletonTrip(result.journey as unknown as JourneyTransformed);
        setFullTrip(skeletonTripId, null);
      }
      setTripPanelResult({
        type: 'update-nights',
        id: '',
        place: displayPlaceName,
        status: success ? 'success' : 'failure',
        nights: 0,
        userMessage: success ? t('content.feedback.stay_add_success_dynamic', {
          place: displayPlaceName
        }) : t('content.feedback.stay_add_error_dynamic', {
          place: displayPlaceName
        })
      });
      trackEvent('maps_add_stay', {
        event_category: 'maps',
        event_label: success ? 'success' : 'failure',
        journey_id: journeyId,
        at_start: atStart,
        at_end: atEnd
      });
    } catch (_error) {
      setTripPanelResult({
        type: 'update-nights',
        id: '',
        place: displayPlaceName,
        status: 'failure',
        nights: 0,
        userMessage: t('content.feedback.stay_add_error_dynamic', {
          place: displayPlaceName
        })
      });
    } finally {
      completeProgressBar();
      await new Promise(resolve => setTimeout(resolve, 1000));
      setOverviewSubmitting(false);
      setIsLoading(false);
    }
  };
  const handleRemoveStay = async () => {
    if (!item) return;
    const itemPlaceName = item.title || displayPlaceName;
    setIsLoading(true);
    setTripPanelResult(null);
    setOverviewDisabled(true);
    setOverviewSubmitting(true);
    startLoader();
    try {
      const result_0 = await removeStayFromSkeletonMutation.mutateAsync({
        journeyId: skeletonTripId,
        atStart,
        atEnd
      });
      const success_0 = result_0.success && result_0.journey;
      if (success_0) {
        setSkeletonTrip(result_0.journey as unknown as JourneyTransformed);
        setFullTrip(skeletonTripId, null);
      }
      setTripPanelResult({
        type: 'update-nights',
        id: String(item.id),
        place: itemPlaceName,
        status: success_0 ? 'success' : 'failure',
        nights: success_0 ? 0 : localNights,
        userMessage: success_0 ? t('content.feedback.stay_remove_success_dynamic', {
          place: itemPlaceName
        }) : t('content.feedback.stay_remove_error_dynamic', {
          place: itemPlaceName
        })
      });
      trackEvent('maps_remove_stay', {
        event_category: 'maps',
        event_label: success_0 ? 'success' : 'failure',
        journey_id: journeyId,
        at_start: atStart,
        at_end: atEnd
      });
    } catch (error) {
      setTripPanelResult({
        type: 'update-nights',
        id: String(item.id),
        place: itemPlaceName,
        status: 'failure',
        nights: localNights,
        userMessage: t('content.feedback.stay_remove_error_dynamic', {
          place: itemPlaceName
        })
      });
      logError(error);
    } finally {
      completeProgressBar();
      await new Promise(resolve => setTimeout(resolve, 1000));
      setOverviewSubmitting(false);
      setOverviewDisabled(false);
      setIsLoading(false);
    }
  };
  const handleChange = (nightsChange: number) => {
    if (canRemoveStay && nightsChange < 0) {
      handleRemoveStay();
      return;
    }
    if (isMaxReached && nightsChange > 0 || isMinReached && nightsChange < 0) return;
    const newLocalNights = localNights + nightsChange;
    if (item && newLocalNights !== item.nights) {
      // if the change causes us to go to a new value, we should disable nights edits across the board
      setOverviewDisabled(true);
    } else {
      // if the change causes us to go back to the original value before we started this edit (pre-debounce expiry), we should reenable nights edits across the board
      setOverviewDisabled(false);
      return;
    }
    setLocalNights(newLocalNights);
    trackEvent('maps_change_nights_button', {
      event_category: 'maps',
      event_label: nightsChange > 0 ? 'plus' : 'minus',
      destination_id: item?.title,
      journey_id: journeyId,
      value: nightsChange
    });
  };
  useEffect(() => {
    const changeDuration = async () => {
      try {
        if (!item || debouncedNights === item.nights) {
          setOverviewDisabled(false);
          setOverviewSubmitting(false);
          setIsLoading(false);
          return;
        }
        setTripPanelResult(null);
        setOverviewSubmitting(true);
        setIsLoading(true);
        startLoader();
        const body = {
          journeyId,
          stayItemId: item.stayItemId,
          newDurationNights: debouncedNights,
          locale
        };
        const result_1 = await changeSkeletonDuration.mutateAsync(body);
        if (result_1?.success && result_1?.journey) {
          setSkeletonTrip(result_1.journey);
          // invalidate the full trip assoc with this skeleton - will no longer match our number of nights
          setFullTrip(skeletonTripId, null);
          setTripPanelResult({
            type: 'update-nights',
            id: String(item.id),
            place: item.title,
            status: 'success',
            nights: debouncedNights,
            userMessage: t('content.feedback.nights_update_success', {
              place: item.title
            })
          });
          trackEvent('maps_change_nights_submit', {
            event_category: 'maps',
            event_label: 'success',
            destination_id: item.title,
            value: debouncedNights,
            journey_id: journeyId
          });
        } else {
          throw new Error(result_1);
        }
      } catch (error_0) {
        if (item) {
          setLocalNights(item.nights);
          setDebouncedNights(item.nights);
          setTripPanelResult({
            type: 'update-nights',
            id: String(item.id),
            place: item.title,
            status: 'failure',
            nights: debouncedNights,
            userMessage: t('content.feedback.nights_update_error', {
              place: item.title
            })
          });
          trackEvent('maps_change_nights_submit', {
            event_category: 'maps',
            event_label: 'failure',
            destination_id: item.title,
            value: debouncedNights,
            journey_id: journeyId
          });
          logError(error_0);
        }
      } finally {
        completeProgressBar();
        await new Promise(resolve => setTimeout(resolve, 1000));
        setOverviewSubmitting(false);
        setOverviewDisabled(false);
        setIsLoading(false);
      }
    };
    changeDuration();
  }, [debouncedNights]);

  // Add stay mode - show "Add Stay" button
  if (isAddStayMode) {
    if (!isAddStayEnabled) return null;
    return <div className={styles.controlsAndLoader}>
        <Button className={clsx(styles.addStayButton, {
        [styles.disabled]: areEditsDisabled
      })} isDisabled={areEditsDisabled} onPress={handleAddStay} aria-label={t('aria.add_stay_at', {
        place: displayPlaceName
      })}>
          <PlusCircleSolid size="small" decorative />
          <span className={styles.addStayText}>{t('journey.add_stay')}</span>
        </Button>
        {isLoading && <div className={styles.loadingIndicator}>
            <div className={styles.loadingDot}></div>
            <div className={styles.loadingDot}></div>
            <div className={styles.loadingDot}></div>
          </div>}
      </div>;
  }

  // Normal mode - show nights adjuster
  return <div className={styles.container}>
      <div className={styles.nightsDisplayContainer}>
        <div className={styles.controlsAndLoader}>
          <div className={clsx(styles.controls, {
          [styles.error]: hasThisPlaceFailed
        })}>
            <Button className={clsx(styles.button, {
            [styles.disabled]: (areEditsDisabled || isMinReached) && !canRemoveStay
          })} isDisabled={(areEditsDisabled || isMinReached) && !canRemoveStay} onPress={() => handleChange(-1)} aria-label={canRemoveStay ? t('journey.remove_stay') : t('aria.decrease_nights_at', {
            place: item.title
          })}>
              {canRemoveStay ? <TrashSolid size="small" decorative /> : <Minus size="small" decorative />}
            </Button>

            <span className={styles.nightsLabel}>{localNights}</span>

            <Button className={clsx(styles.button, {
            [styles.disabled]: areEditsDisabled || isMaxReached
          })} isDisabled={areEditsDisabled || isMaxReached} onPress={() => handleChange(1)} aria-label={t('aria.increase_nights_at', {
            place: item.title
          })}>
              <Plus size="small" decorative />
            </Button>
          </div>
          {isLoading && <div className={styles.loadingIndicator}>
              <div className={styles.loadingDot}></div>
              <div className={styles.loadingDot}></div>
              <div className={styles.loadingDot}></div>
            </div>}
        </div>
        <span className={styles.nightsLabel}>{t('journey.nights_short')}</span>
      </div>
    </div>;
}